import { toolDisplayName } from "@agentguard/shared-lib/aiTools";
import { TOOL_STATUS_LABEL, formatWhen } from "./aiUsage/format";
import { hasOwn } from "./aiUsage/hasOwn";

// THE Convex-auditEvents → Security-Room-row normalizer.
//
// Convex stores a verdict where it belongs for the writer: nested, e.g.
// `details.verdict.decision` for `policy-decision`, `details.action` for
// `egress-violation`, `details.decision` for `approval-resolved`. Every
// Security Room selector and filter, however, reads a FLAT
// `row.severity ?? row.decision` (and `row.patternId` for DLP) — a shape
// inherited from the legacy Bun-host `/api/admin/audit` payload.
//
// Nothing bridged the two. The snapshot mapper projected only
// `{id, type, actor, subject, detail, recordedAt}`, so `decision` and
// `severity` were undefined on every row and the whole severity-aware surface
// silently read as empty-or-informational:
//   - Blocks page                 → always "0 blocks in the last 24h"
//   - Overview "Blocks (24h)"     → always 0
//   - Overview "Critical · Now"   → always empty
//   - Overview activity chart     → denies counted as ALLOWED
//   - Audit log "Block" filter    → "No events match."
//   - DLP page                    → every hit grouped under "unknown"
//
// One function, one place. Add a new audit `type` here and every consumer
// picks it up.

export type AuditDecision = "allow" | "ask" | "deny";
export type AuditSeverity = "allow" | "ask" | "deny" | "crit" | "info";

export interface NormalizedAuditRow extends Record<string, unknown> {
  id: string;
  type: string;
  actor: string | null;
  subject: string | null;
  detail: Record<string, unknown>;
  recordedAt: string;
  createdAt: string;
  // Flat fields the selectors/filters read:
  decision?: AuditDecision;
  severity: AuditSeverity;
  /** false when the verdict was recorded in observe mode (shadow enforcement). */
  enforced: boolean;
  command?: string;
  ruleId?: string;
  packId?: string;
  reason?: string;
  agent?: string;
  patternId?: string;
  dlpTier?: string;
}

function asRecord(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}

// First value that is actually usable — skips null, undefined and "".
function firstPresent(values: unknown[]): unknown {
  for (const v of values) {
    if (v === null || v === undefined) continue;
    // Whitespace-only is as unusable as empty — a row with `at: "  "` should
    // fall through to the next field, not stamp render time.
    if (typeof v === "string" && v.trim().length === 0) continue;
    return v;
  }
  return undefined;
}

function str(v: unknown): string | undefined {
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

function asDecision(v: unknown): AuditDecision | undefined {
  // Case-insensitive: an external writer emitting "DENY" would otherwise fall
  // through to `info` and disappear from the Blocks page — a verdict lost to
  // capitalisation.
  const d = typeof v === "string" ? v.toLowerCase() : v;
  if (d === "allow" || d === "allowed") return "allow";
  if (d === "ask") return "ask";
  if (d === "deny" || d === "denied" || d === "blocked" || d === "block") return "deny";
  return undefined;
}

// Epoch seconds vs milliseconds. A 10-digit value is seconds; Date treats every
// number as milliseconds, so an epoch-seconds timestamp lands in January 1970 —
// which then falls outside every 24h window and disappears from the page.
// 1e11 ms is 1973, and no real audit row predates that, so anything smaller is
// seconds.
const MS_THRESHOLD = 1e11;

function epochToIso(epoch: number): string {
  return new Date(epoch < MS_THRESHOLD ? epoch * 1000 : epoch).toISOString();
}

export function isoFrom(n: unknown): string {
  // `new Date(undefined).toISOString()` throws RangeError and takes the whole
  // render down, so every path is guarded.
  if (typeof n === "number" && Number.isFinite(n)) return epochToIso(n);
  if (typeof n === "string" && n) {
    // A stringified epoch (`"1710000000000"`) is NOT parseable by Date.parse,
    // so it fell through to "now" — silently replacing a historical event's
    // timestamp with render time in an audit view.
    // 10+ digits only. A short numeric string like "2024" is a year, not an
    // epoch, and Date.parse already reads it correctly.
    if (/^\d{10,}$/.test(n)) {
      // 13+ digits is epoch MILLISECONDS; exactly 10 is epoch SECONDS, and
      // treating those as ms lands the event in January 1970.
      const epoch = Number(n);
      if (Number.isFinite(epoch)) return epochToIso(epoch);
    }
    // Validate before passing through. Returning an unparseable string let a
    // downstream `new Date(x).toISOString()` throw RangeError at render — the
    // very crash the guard in SecurityRoomViewNew was added to prevent.
    const parsed = Date.parse(n);
    if (Number.isFinite(parsed)) return new Date(parsed).toISOString();
  }
  return new Date().toISOString();
}

// `deny`/`denied`/`block`/`blocked` as a whole word anywhere in the type,
// separator-agnostic: `terminal.firewall.deny`, `fs.write.denied`,
// `firewall_deny`, `blocked-command` all match; `undeny` and `deniable` do not.
const DENY_IN_TYPE = /(^|[._\-])(deny|denied|block|blocked)([._\-]|$)/i;

// Audit types that carry an enforcement verdict, and where each one keeps it.
// `policy-decision` nests under `details.verdict`; host-emitted firewall/egress
// rows put it at the top of `details`; approvals use past-tense wording.
function extractDecision(
  type: string,
  detail: Record<string, unknown>
): {
  decision?: AuditDecision;
  ruleId?: string;
  packId?: string;
  reason?: string;
} {
  const verdict = asRecord(detail.verdict);
  const decision =
    asDecision(verdict.decision) ??
    asDecision(detail.decision) ??
    asDecision(detail.action) ??
    // Some events encode the verdict in the TYPE and carry no decision field.
    // Matching a suffix missed most of them — the real writers are
    // `terminal.firewall.deny`, `fs.write.denied` and `firewall_deny`, and
    // `.endsWith(".deny")` catches only the first. A deny that renders as
    // "info" disappears from the Blocks page and the 24h metric, which is the
    // exact failure this file exists to remove. Match the word instead of
    // guessing at the separator.
    (DENY_IN_TYPE.test(type) ? "deny" : undefined);
  return {
    decision,
    ruleId: str(verdict.ruleId) ?? str(detail.ruleId) ?? str(detail.rule),
    packId: str(verdict.packId) ?? str(detail.packId),
    reason: str(verdict.reason) ?? str(detail.reason) ?? str(detail.why),
  };
}

// DLP tiers that mean "a live secret was exposed". Sourced from the tier the
// scanner itself assigns (shared-lib dlpScan) rather than a hand-copied list of
// pattern ids that drifts the moment a pattern is added.
function isHighTier(tier: string | undefined): boolean {
  return tier === "high";
}

export function normalizeAuditRow(row: {
  id?: unknown;
  _id?: unknown;
  type?: unknown;
  actorUserId?: unknown;
  actor?: unknown;
  subject?: unknown;
  details?: unknown;
  at?: unknown;
  recordedAt?: unknown;
  createdAt?: unknown;
  ts?: unknown;
  lastSeenAt?: unknown;
}): NormalizedAuditRow {
  // A null/undefined row must not take the page down. Every Security Room
  // surface renders through this, so one malformed row would blank the whole
  // screen — which reads as "nothing happened", the exact failure this file
  // exists to prevent.
  const safeRow = (row ?? {}) as NonNullable<typeof row>;
  const type = str(safeRow.type) ?? "unknown";
  // Accept either shape. A normalized row exposes `detail` (singular); reading
  // only `details` would silently drop the verdict if a row were normalized
  // twice — losing the decision rather than failing loudly.
  // An EMPTY `details` must not mask a populated `detail`: `??` only falls
  // through on nullish, so a re-normalized row carrying `details: {}` alongside
  // its real `detail` lost the verdict.
  const detailsA = asRecord(safeRow.details);
  const detailsB = asRecord((safeRow as { detail?: unknown }).detail);
  const detail = Object.keys(detailsA).length > 0 ? detailsA : detailsB;
  // Read every timestamp field a row might carry, not just `at`.
  //
  // Convex auditEvents always use `at`, but the legacy store shape and the SSE
  // activity feed use `recordedAt` / `createdAt` / `ts` / `lastSeenAt`. Reading
  // only `at` stamped those rows with RENDER TIME — which then sailed through
  // every window filter, because "now" is never older than the cutoff. A
  // falsified execution time on an audit row is worse than a missing one.
  // firstPresent, not `??`: an empty string is not nullish, so `at: ""` would
  // short-circuit the chain and the real timestamp in a later field would never
  // be consulted.
  const ts = isoFrom(
    firstPresent([
      safeRow.at,
      safeRow.recordedAt,
      safeRow.createdAt,
      safeRow.ts,
      safeRow.lastSeenAt,
    ])
  );

  const { decision, ruleId, packId, reason } = extractDecision(type, detail);
  // `evaluate` in observe mode records the verdict it WOULD have applied and
  // returns allow. Keep the verdict (operators need to see shadow enforcement)
  // but mark it unenforced so the UI never claims a command was stopped.
  // Checked in BOTH places. `evaluate` writes observed/mode at the top level of
  // details today, but the verdict object is the other plausible home for them
  // and a writer moving them there would silently turn every shadow verdict
  // into a reported block — the precise misreporting this file removes.
  const verdictObj = asRecord(detail.verdict);
  // An explicit `enforced: false` is honoured too. A writer that states the
  // fact directly must not be overruled by the absence of the flags this code
  // happens to look for — the default is "enforced", so every unhandled shape
  // errs toward over-reporting blocks.
  // Tolerant comparisons on BOTH sides. Strict equality against `false` /
  // `true` / "observe" meant a writer emitting "false", "TRUE" or "OBSERVE"
  // slipped past every check and its shadow verdict was reported as an enforced
  // block. The default here is "enforced", so any shape this misses errs toward
  // OVER-reporting what the engine stopped — the one direction a blocks metric
  // must not err in.
  const isFalsey = (v: unknown) => v === false || String(v).toLowerCase() === "false";
  const isTruthy = (v: unknown) => v === true || String(v).toLowerCase() === "true";
  const isObserve = (v: unknown) => String(v).toLowerCase() === "observe";
  const enforced = !(
    isFalsey(detail.enforced) ||
    isFalsey(verdictObj.enforced) ||
    isTruthy(detail.observed) ||
    isTruthy(verdictObj.observed) ||
    isObserve(detail.mode) ||
    isObserve(verdictObj.mode)
  );

  // Convex DLP writers emit `patternIds` (an array); the scalar is the legacy
  // shape. Surface the first id on the flat row so consumers reading
  // `row.patternId` see a DLP hit either way — normalizeDlpRows still fans the
  // full array out for the page.
  const patternIdList = Array.isArray(detail.patternIds)
    ? (detail.patternIds as unknown[]).filter((x): x is string => typeof x === "string" && !!x)
    : [];
  const patternId = str(detail.patternId) ?? patternIdList[0];
  const dlpTier = str(detail.tier);

  let severity: AuditSeverity;
  if (type.startsWith("dlp")) {
    severity = isHighTier(dlpTier) ? "crit" : "ask";
  } else if (decision === "deny") {
    severity = "deny";
  } else if (decision === "ask") {
    severity = "ask";
  } else if (decision === "allow") {
    severity = "allow";
  } else {
    severity = "info";
  }

  return {
    // A row with neither id nor _id used to become "", so every such row
    // shared one React key and the list reconciled incorrectly. Derive a
    // stable-enough key from what the row does have.
    id: String(firstPresent([safeRow.id, safeRow._id]) ?? `${type}:${ts}`),
    type,
    actor: str(safeRow.actorUserId) ?? str(safeRow.actor) ?? null,
    subject: str(safeRow.subject) ?? null,
    detail,
    recordedAt: ts,
    createdAt: ts,
    decision,
    severity,
    enforced,
    command: str(safeRow.subject) ?? str(detail.command),
    ruleId,
    packId,
    reason,
    agent: str(detail.agent),
    patternId,
    dlpTier,
  };
}

// A `dlp-*` audit row projected for the DLP page, FANNED OUT to one entry per
// pattern that fired. A single redaction event can trip several patterns, and
// the page groups by pattern, so one row must become several.
//
// Before this, the mapper read `details.patternId` — a field the redaction
// writers never set — so every hit in the product collapsed into one group
// literally labelled "unknown", at tier "medium". A leaked AWS key and a
// leaked Stripe key were indistinguishable, and neither rendered as critical.
//
// Rows that genuinely carry no pattern (scan errors) fall back to a stable,
// attributable key rather than "unknown".
export function normalizeDlpRows(
  row: NormalizedAuditRow
): Array<NormalizedAuditRow & { patternId: string }> {
  const detail = asRecord(row.detail);
  const ids = Array.isArray(detail.patternIds)
    ? (detail.patternIds as unknown[]).filter((x): x is string => typeof x === "string" && !!x)
    : [];
  if (row.patternId) ids.push(row.patternId);

  const hasPatternTiers =
    !!detail.patternTiers &&
    typeof detail.patternTiers === "object" &&
    Object.keys(detail.patternTiers as object).length > 0;
  const tiers = hasPatternTiers ? (detail.patternTiers as Record<string, unknown>) : {};

  const unique = Array.from(new Set(ids));
  if (unique.length > 0) {
    return unique.map((patternId) => {
      // Each hit renders at ITS OWN tier. Inheriting the event's worst tier
      // made a low-tier match that merely co-occurred with a real secret
      // render as critical, which is the same class of misreporting this
      // whole change exists to stop.
      // Two different situations, two different answers:
      //
      //   patternTiers PRESENT but missing this id → the writer tiered the
      //     event and did not tier this pattern, so it is "low". Inheriting
      //     the event tier here re-elevated a pattern that merely co-occurred
      //     with a real secret.
      //
      //   patternTiers ABSENT entirely → a row written before per-pattern
      //     tiers existed. The event tier is the only signal there is, and
      //     defaulting to "low" would silently downgrade every historical
      //     high-tier hit.
      const own = tiers[patternId];
      const dlpTier = typeof own === "string" ? own : hasPatternTiers ? "low" : row.dlpTier;
      return {
        ...row,
        // Unique per fanned row. Every pattern from one event inherited the
        // SAME `row.id`, so React saw duplicate keys across a group and
        // reconciled the list incorrectly.
        id: `${row.id}:${patternId}`,
        patternId,
        dlpTier,
        severity: isHighTier(dlpTier) ? ("crit" as const) : ("ask" as const),
      };
    });
  }

  // No pattern named. Check the ERROR type first: `source` is populated on scan
  // -error rows too, so testing it first meant the scan-error branch was dead
  // and those rows were filed under a normal source group.
  const fallback =
    row.type === "dlp-redaction-error" ? "scan-error" : (str(detail.source) ?? row.type);
  return [{ ...row, patternId: fallback }];
}

// The AI gateway's control events (convex/observability/controls.ts) in words,
// for the Audit log. Their details carry before and after values, not a
// verdict (no top-level mode, decision or action field), so normalizeAuditRow
// leaves them at severity "info". A changed call retention is a
// gateway.policy.changed event too. Stored values are shown as stored: a
// session key is never parsed, and a tool key or status is looked up by own
// key only, since "constructor" is a valid tool key.
function words(v: unknown, unit = ""): string {
  if (v === null || v === undefined || v === "") return "not set";
  return `${String(v)}${unit}`;
}

function statusWords(v: unknown): string {
  return typeof v === "string" && hasOwn(TOOL_STATUS_LABEL, v) ? TOOL_STATUS_LABEL[v] : words(v);
}

// A time as the Security Room shows one (formatWhen), or null.
function whenOrNull(v: unknown): string | null {
  return typeof v === "number" && Number.isFinite(v) ? formatWhen(v) || null : null;
}

// Who is reading the audit log: an admin opens any session, a member only
// their own (convex/observability/queries.ts getSession).
export interface AuditViewer {
  userId: string | null;
  isAdmin: boolean;
}

// A session id the Security Room route takes (useSrRouteHash PARAM_RE), so
// a stopped session can be linked; anything else gets no link.
const SESSION_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;

export function aiAuditDescription(
  type: string,
  detail: Record<string, unknown>,
  // Names for the user ids the events carry (the page passes usePeople's).
  nameFor: (userId: string) => string = (userId) => userId,
  // The reader, so a session is linked only where it would open for them;
  // with no reader, nothing is linked.
  viewer?: AuditViewer
): {
  summary: string;
  details?: Record<string, string>;
  link?: { href: string; label: string };
} | null {
  const person = (v: unknown) => (typeof v === "string" && v ? nameFor(v) : words(v));
  switch (type) {
    case "gateway.policy.changed":
      if (detail.field === "mode") {
        return {
          summary: `AI gateway mode set to ${words(detail.after)}`,
          details: { was: words(detail.before) },
        };
      }
      if (detail.field === "callRetentionDays") {
        return {
          summary: `AI call retention set to ${words(detail.after, " days")}`,
          details: { was: words(detail.before, " days") },
        };
      }
      return { summary: "AI gateway setting changed" };
    case "gateway.org.paused":
      return typeof detail.reason === "string" && detail.reason
        ? { summary: "AI traffic paused", details: { reason: detail.reason } }
        : { summary: "AI traffic paused" };
    case "gateway.org.resumed": {
      // Which pause this ended: who set it and when.
      const details: Record<string, string> = {};
      if (typeof detail.pausedBy === "string" && detail.pausedBy) {
        details["paused by"] = person(detail.pausedBy);
      }
      const at = whenOrNull(detail.pausedAt);
      if (at) details["paused at"] = at;
      return Object.keys(details).length > 0
        ? { summary: "AI traffic resumed", details }
        : { summary: "AI traffic resumed" };
    }
    case "gateway.session.stopped": {
      const id = detail.sessionId;
      const opensForViewer =
        typeof id === "string" &&
        SESSION_ID_RE.test(id) &&
        viewer !== undefined &&
        (viewer.isAdmin || (viewer.userId !== null && detail.owner === viewer.userId));
      return {
        summary: "AI session stopped",
        details: {
          session: words(detail.sessionKey),
          owner: person(detail.owner),
          tool: typeof detail.tool === "string" ? toolDisplayName(detail.tool) : "unknown",
        },
        ...(opensForViewer
          ? { link: { href: `#ai-sessions/${id}`, label: "Open the session" } }
          : {}),
      };
    }
    case "ai.tool.status_changed": {
      const tool = typeof detail.tool === "string" ? toolDisplayName(detail.tool) : "A tool";
      return {
        summary: `${tool} marked ${statusWords(detail.after)}`,
        details: { was: statusWords(detail.before) },
      };
    }
    default:
      return null;
  }
}
